import { Box } from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';

import {
  typeguards as commonTypeguards,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Bank, CreditAgreement } from '@finley-technologies/finley-types';

import { BorrowingBaseSummaryAccordion } from './BorrowingBaseSummaryAccordion';
import { BorrowingBaseSummaryAccordionRow } from './BorrowingBaseSummaryAccordionRow';
import { BorrowingBaseSummaryAccordionSubRow } from './BorrowingBaseSummaryAccordionSubRow';
import { BorrowingBaseSummarySectionHeader } from './BorrowingBaseSummarySectionHeader';
import {
  getContributingBankAccountAdvanceRates,
  getBankAccountCashAdvanceRateSubRowLabel,
  getColumnValuesForBorrowingBaseReportField,
  BorrowingBaseSummaryInput,
} from './utils';

type BorrowingBaseSummaryCashProps = {
  borrowingBaseReports: BorrowingBaseSummaryInput;
  creditAgreement: CreditAgreement.DomainEntity.CreditAgreementFullArtifact;
  showComparisonAndDifferenceColumns: boolean;
  showValueAndReferenceColumnsOnly: boolean;
};
export const BorrowingBaseSummaryCash = ({
  borrowingBaseReports,
  creditAgreement,
  showComparisonAndDifferenceColumns,
  showValueAndReferenceColumnsOnly,
}: BorrowingBaseSummaryCashProps) => {
  const minuendBankAccountCashBalances =
    borrowingBaseReports.minuend.bankAccountCashBalances;

  const {
    facilityMechanics: {
      parties: { borrowers },
    },
  } = creditAgreement;

  const bankAccountAdvanceRates = getContributingBankAccountAdvanceRates({
    borrowers,
  });

  const contributingBankAccountCashBalances =
    minuendBankAccountCashBalances?.filter(({ accountType }) =>
      commonTypeguards.isBorrowingBaseContributionAccountType(accountType),
    ) ?? [];

  // group balances by account type
  const groupedContributingBankAccountCashBalances = contributingBankAccountCashBalances.reduce<
    Record<string, number>
  >((acc, item) => {
    const { accountType } = item;
    // eslint-disable-next-line no-param-reassign
    acc[accountType] = (acc[accountType] ?? 0) + item.balance;
    return acc;
  }, {});

  const collectionsAccountTypeExists = !!groupedContributingBankAccountCashBalances[
    Bank.BorrowerAccountType.Collections
  ];

  return (
    <Box w="100%">
      <BorrowingBaseSummarySectionHeader headerLabel="Cash" />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Pre-advance Cash Contribution"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'totalPreAdvanceCashContribution',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
            fieldName="totalPreAdvanceCashContribution"
          />
        )}
        accordionPanel={
          <>
            {/* if collections account(s) do not exist, render the borrowing base reserve amount on its own */}
            {/* since it will not get rendered with the collections account balance below */}
            {!collectionsAccountTypeExists && (
              <BorrowingBaseSummaryAccordionSubRow
                label="Borrowing Base Reserve Amount"
                columnValues={getColumnValuesForBorrowingBaseReportField({
                  borrowingBaseReports,
                  fieldName: 'borrowingBaseReserveAmount',
                })}
                valueType="currency"
                showComparisonAndDifferenceColumns={
                  showComparisonAndDifferenceColumns
                }
                showValueAndReferenceColumnsOnly={
                  showValueAndReferenceColumnsOnly
                }
                fieldName="borrowingBaseReserveAmount"
              />
            )}
            {Object.entries(groupedContributingBankAccountCashBalances).map(
              ([accountType, totalBalance]) => {
                const balanceForAccountTypeSubtrahend =
                  borrowingBaseReports.subtrahend?.bankAccountCashBalances?.reduce(
                    (sum, cashBalance) => {
                      return cashBalance.accountType === accountType
                        ? sum + (cashBalance.balance || 0)
                        : sum;
                    },
                    0,
                  ) ?? 0;

                const balanceForAccountTypeReference =
                  borrowingBaseReports.reference?.bankAccountCashBalances?.reduce(
                    (sum, cashBalance) => {
                      return cashBalance.accountType === accountType
                        ? sum + (cashBalance.balance || 0)
                        : sum;
                    },
                    0,
                  ) ?? 0;

                // Always render borrowing base reserve right under collections
                if (accountType === Bank.BorrowerAccountType.Collections) {
                  return (
                    <React.Fragment key={`balance-${accountType}`}>
                      <BorrowingBaseSummaryAccordionSubRow
                        label="Total Pre-reserve Collections Cash Balance"
                        columnValues={{
                          minuendValue: totalBalance,
                          subtrahendValue: balanceForAccountTypeSubtrahend,
                          referenceValue: balanceForAccountTypeReference,
                        }}
                        valueType="currency"
                        showComparisonAndDifferenceColumns={
                          showComparisonAndDifferenceColumns
                        }
                        showValueAndReferenceColumnsOnly={
                          showValueAndReferenceColumnsOnly
                        }
                        fieldName="totalCollectionsAccountCash"
                      />
                      <BorrowingBaseSummaryAccordionSubRow
                        label="Borrowing Base Reserve Amount"
                        columnValues={getColumnValuesForBorrowingBaseReportField(
                          {
                            borrowingBaseReports,
                            fieldName: 'borrowingBaseReserveAmount',
                          },
                        )}
                        valueType="currency"
                        showComparisonAndDifferenceColumns={
                          showComparisonAndDifferenceColumns
                        }
                        showValueAndReferenceColumnsOnly={
                          showValueAndReferenceColumnsOnly
                        }
                        fieldName="borrowingBaseReserveAmount"
                      />
                    </React.Fragment>
                  );
                }

                return (
                  <BorrowingBaseSummaryAccordionSubRow
                    key={`balance-${accountType}`}
                    label={`${commonUtils.toTitleCase(
                      accountType,
                    )} Cash Balance`}
                    columnValues={{
                      minuendValue: totalBalance,
                      subtrahendValue: balanceForAccountTypeSubtrahend,
                      referenceValue: balanceForAccountTypeReference,
                    }}
                    valueType="currency"
                    showComparisonAndDifferenceColumns={
                      showComparisonAndDifferenceColumns
                    }
                    showValueAndReferenceColumnsOnly={
                      showValueAndReferenceColumnsOnly
                    }
                    fieldName="cashBalanceForAccount"
                  />
                );
              },
            )}
          </>
        }
      />

      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Effective Cash Advance Rate"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'effectiveCashAdvanceRate',
            })}
            valueType="percentage"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
            fieldName="effectiveCashAdvanceRate"
          />
        )}
        accordionPanel={
          <Box>
            {_.map(
              bankAccountAdvanceRates,
              ({
                accountType,
                cashAdvanceRate: bankAccountCashAdvanceRate,
                bankAccountId,
              }) => {
                return (
                  <BorrowingBaseSummaryAccordionSubRow
                    key={`sub-row-cash-advance-rate-${accountType}-${bankAccountId}`}
                    label={getBankAccountCashAdvanceRateSubRowLabel(
                      accountType,
                    )}
                    columnValues={{
                      minuendValue: bankAccountCashAdvanceRate,
                      subtrahendValue: undefined,
                      referenceValue: undefined,
                    }}
                    valueType="string"
                    showComparisonAndDifferenceColumns={
                      showComparisonAndDifferenceColumns
                    }
                    showValueAndReferenceColumnsOnly={
                      showValueAndReferenceColumnsOnly
                    }
                    fieldName="cashAdvanceRate"
                  />
                );
              },
            )}
          </Box>
        }
      />
      <BorrowingBaseSummaryAccordion
        accordionButton={({ hasAccordionPanel, isExpanded }) => (
          <BorrowingBaseSummaryAccordionRow
            label="Total Cash Borrowing Base Amount"
            columnValues={getColumnValuesForBorrowingBaseReportField({
              borrowingBaseReports,
              fieldName: 'totalCashBorrowingBaseAmount',
            })}
            valueType="currency"
            hasAccordionPanel={hasAccordionPanel}
            isExpanded={isExpanded}
            showComparisonAndDifferenceColumns={
              showComparisonAndDifferenceColumns
            }
            showValueAndReferenceColumnsOnly={showValueAndReferenceColumnsOnly}
            fieldName="totalCashBorrowingBaseAmount"
          />
        )}
      />
    </Box>
  );
};
